import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './MinhasImagensNocaute.css';

/**
 * Página para jogador fazer upload e gerenciar suas imagens de nocaute
 */

const MinhasImagensNocaute = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [imagens, setImagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ nocaute: false, eliminacao: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarImagens();
  }, []);

  const carregarImagens = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/imagens-nocautes/minhas');
      setImagens(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar imagens:', err);
      setError(err.response?.data?.error || 'Erro ao carregar imagens');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (tipo, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar formato (imagens e vídeos MP4)
    const allowedTypes = ['image/gif', 'image/webp', 'image/png', 'image/jpeg', 'image/jpg', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato não permitido. Use GIF, WebP, PNG, JPEG ou MP4.');
      return;
    }

    // Validar tamanho (16MB para vídeos, 10MB para imagens)
    const maxSize = file.type === 'video/mp4' ? 16 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = file.type === 'video/mp4' ? '16MB' : '10MB';
      setError(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}.`);
      return;
    }

    try {
      setUploading({ ...uploading, [tipo]: true });
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('imagem', file);
      formData.append('tipo', tipo);

      // Debug: verificar FormData
      console.log('📤 Enviando upload:', {
        tipo: tipo,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      const response = await apiClient.post('/imagens-nocautes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(`Imagem de ${tipo === 'nocaute' ? 'nocaute' : 'eliminação'} enviada! Aguarde aprovação do admin.`);
      setTimeout(() => setSuccess(''), 5000);
      
      // Recarregar lista
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setError(err.response?.data?.error || 'Erro ao fazer upload da imagem');
    } finally {
      setUploading({ ...uploading, [tipo]: false });
      // Limpar input
      event.target.value = '';
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta imagem?')) {
      return;
    }

    try {
      await apiClient.delete(`/imagens-nocautes/${id}`);
      setSuccess('Imagem deletada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError(err.response?.data?.error || 'Erro ao deletar imagem');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'aprovada':
        return <span className="status-badge aprovada">✅ Aprovada</span>;
      case 'pendente':
        return <span className="status-badge pendente">⏳ Pendente</span>;
      case 'rejeitada':
        return <span className="status-badge rejeitada">❌ Rejeitada</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const getImagemPorTipo = (tipo) => {
    return imagens.find(img => img.tipo === tipo && img.status === 'aprovada') ||
           imagens.find(img => img.tipo === tipo);
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container minhas-imagens-container">
      <div className="page-header">
        <h1>🎨 Minhas Imagens de Nocaute</h1>
        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="info-box">
        <p>📸 Faça upload de imagens ou vídeos personalizados para seus nocautes e eliminações!</p>
        <p>Os arquivos serão enviados para aprovação do admin antes de serem usados.</p>
        <p><strong>Formatos aceitos:</strong> GIF, WebP, PNG, JPEG, MP4 | <strong>Tamanho máximo:</strong> 10MB (imagens) / 16MB (vídeos)</p>
        <p><strong>ℹ️ Nota:</strong> Imagens serão redimensionadas automaticamente para 200x200px. Vídeos MP4 mantêm tamanho original. Dimensão mínima para imagens: 100px.</p>
      </div>

      <div className="imagens-grid">
        {/* Imagem de Nocaute */}
        <div className="imagem-card">
          <h2>🥊 Imagem de Nocaute</h2>
          
          {getImagemPorTipo('nocaute') ? (
            <div className="imagem-preview-container">
              <img 
                src={getImagemPorTipo('nocaute').url}
                alt="Imagem de nocaute"
                className="imagem-preview"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="imagem-placeholder" style={{ display: 'none' }}>
                Imagem não encontrada
              </div>
              <div className="imagem-info">
                {getStatusBadge(getImagemPorTipo('nocaute').status)}
                {getImagemPorTipo('nocaute').motivo_rejeicao && (
                  <p className="motivo-rejeicao">
                    <strong>Motivo da rejeição:</strong> {getImagemPorTipo('nocaute').motivo_rejeicao}
                  </p>
                )}
                <p className="imagem-detalhes">
                  {getImagemPorTipo('nocaute').nome_arquivo} 
                  ({(getImagemPorTipo('nocaute').tamanho_arquivo / 1024).toFixed(2)} KB)
                </p>
              </div>
              <div className="imagem-actions">
                <label className="btn btn-primary">
                  {uploading.nocaute ? 'Enviando...' : '📤 Substituir Imagem'}
                  <input
                    type="file"
                    accept=".gif,.webp,.png,.jpeg,.jpg,.mp4,image/gif,image/webp,image/png,image/jpeg,image/jpg,video/mp4"
                    onChange={(e) => handleFileSelect('nocaute', e)}
                    style={{ display: 'none' }}
                    disabled={uploading.nocaute}
                  />
                </label>
                <button
                  onClick={() => handleDeletar(getImagemPorTipo('nocaute').id)}
                  className="btn btn-danger"
                >
                  🗑️ Deletar
                </button>
              </div>
            </div>
          ) : (
            <div className="imagem-upload-area">
              <label className="upload-label">
                <input
                  type="file"
                  accept=".gif,.webp,.png,image/gif,image/webp,image/png"
                  onChange={(e) => handleFileSelect('nocaute', e)}
                  style={{ display: 'none' }}
                  disabled={uploading.nocaute}
                />
                <div className="upload-placeholder">
                  {uploading.nocaute ? (
                    <div>⏳ Enviando...</div>
                  ) : (
                    <>
                      <div className="upload-icon">📤</div>
                      <div>Clique para fazer upload</div>
                      <div className="upload-hint">GIF, WebP, PNG ou JPEG (máx 10MB, será redimensionado)</div>
                    </>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Imagem de Eliminação */}
        <div className="imagem-card">
          <h2>💀 Imagem de Eliminação</h2>
          
          {getImagemPorTipo('eliminacao') ? (
            <div className="imagem-preview-container">
              <img 
                src={getImagemPorTipo('eliminacao').url}
                alt="Imagem de eliminação"
                className="imagem-preview"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="imagem-placeholder" style={{ display: 'none' }}>
                Imagem não encontrada
              </div>
              <div className="imagem-info">
                {getStatusBadge(getImagemPorTipo('eliminacao').status)}
                {getImagemPorTipo('eliminacao').motivo_rejeicao && (
                  <p className="motivo-rejeicao">
                    <strong>Motivo da rejeição:</strong> {getImagemPorTipo('eliminacao').motivo_rejeicao}
                  </p>
                )}
                <p className="imagem-detalhes">
                  {getImagemPorTipo('eliminacao').nome_arquivo} 
                  ({(getImagemPorTipo('eliminacao').tamanho_arquivo / 1024).toFixed(2)} KB)
                </p>
              </div>
              <div className="imagem-actions">
                <label className="btn btn-primary">
                  {uploading.eliminacao ? 'Enviando...' : '📤 Substituir Imagem'}
                  <input
                    type="file"
                    accept=".gif,.webp,.png,.jpeg,.jpg,.mp4,image/gif,image/webp,image/png,image/jpeg,image/jpg,video/mp4"
                    onChange={(e) => handleFileSelect('eliminacao', e)}
                    style={{ display: 'none' }}
                    disabled={uploading.eliminacao}
                  />
                </label>
                <button
                  onClick={() => handleDeletar(getImagemPorTipo('eliminacao').id)}
                  className="btn btn-danger"
                >
                  🗑️ Deletar
                </button>
              </div>
            </div>
          ) : (
            <div className="imagem-upload-area">
              <label className="upload-label">
                <input
                  type="file"
                  accept=".gif,.webp,.png,image/gif,image/webp,image/png"
                  onChange={(e) => handleFileSelect('eliminacao', e)}
                  style={{ display: 'none' }}
                  disabled={uploading.eliminacao}
                />
                <div className="upload-placeholder">
                  {uploading.eliminacao ? (
                    <div>⏳ Enviando...</div>
                  ) : (
                    <>
                      <div className="upload-icon">📤</div>
                      <div>Clique para fazer upload</div>
                      <div className="upload-hint">GIF, WebP, PNG ou JPEG (máx 10MB, será redimensionado)</div>
                    </>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MinhasImagensNocaute;
